import dayjs from "dayjs";
import { Badge, Box, Group, Paper, Stack, Text, Timeline } from "@mantine/core";

const labelBadge = (value, color = "gray") => (
  <Badge variant="light" color={color} size="xs" radius="sm" tt="uppercase" fw={600}>
    {String(value || "—").replaceAll("_", " ")}
  </Badge>
);

const activityStatusColor = (status) => {
  const key = String(status || "").toUpperCase();
  if (key === "DONE") return "green";
  if (key === "SCHEDULED") return "orange";
  return "gray";
};

const followupStatusColor = (status) => {
  const key = String(status || "").toUpperCase();
  if (key === "DONE") return "green";
  if (key === "UPCOMING") return "blue";
  if (key === "OVERDUE") return "red";
  return "gray";
};

const EVENT_COLOR = {
  LEAD_CREATED: "green",
  LEAD_UPDATED: "blue",
  QUOTATION_CREATED: "violet",
  QUOTATION_SHARED: "teal",
  FOLLOWUP_SCHEDULED: "orange",
};

const metaText = (value) => {
  if (value == null || value === "") return "—";
  const text = String(value);
  return dayjs(text).isValid() && /^\d{4}-\d{2}-\d{2}/.test(text)
    ? dayjs(text).format(text.includes("T") ? "DD MMM YYYY, h:mm a" : "DD MMM YYYY")
    : text.replaceAll("_", " ");
};

const TimelineShell = ({ items, isLoading, emptyLabel, renderCard }) => {
  if (isLoading) {
    return (
      <Text size="sm" c="dimmed">
        Loading...
      </Text>
    );
  }
  if (!items.length) {
    return (
      <Text size="sm" c="dimmed">
        {emptyLabel}
      </Text>
    );
  }

  return (
    <Stack gap={0} pl={4}>
      {items.map((item, index) => (
        <Box key={item.id ?? index} pos="relative" pb="md">
          <Box
            pos="absolute"
            left={0}
            top={10}
            w={10}
            h={10}
            style={{ borderRadius: "50%", background: "#F97316", zIndex: 1 }}
          />
          {index < items.length - 1 ? (
            <Box
              pos="absolute"
              left={4}
              top={20}
              bottom={0}
              w={2}
              style={{ background: "#FED7AA" }}
            />
          ) : null}
          <Paper withBorder p="sm" ml={20} radius="md" shadow="xs">
            {renderCard(item)}
          </Paper>
        </Box>
      ))}
    </Stack>
  );
};

export const LeadActivityTimeline = ({ items, isLoading }) => (
  <TimelineShell
    items={items}
    isLoading={isLoading}
    emptyLabel="No activities yet."
    renderCard={(activity) => (
      <Stack gap={6}>
        <Group gap={6}>
          {labelBadge(activity.activity_type, "blue")}
          {labelBadge(activity.status, activityStatusColor(activity.status))}
        </Group>
        <Text size="xs" c="dimmed">
          {activity.activity_date}
          {activity.done_by_name ? ` · by ${activity.done_by_name}` : ""}
        </Text>
        <Text size="sm">{activity.summary}</Text>
      </Stack>
    )}
  />
);

export const LeadFollowupTimeline = ({ items, isLoading }) => (
  <TimelineShell
    items={items}
    isLoading={isLoading}
    emptyLabel="No follow-ups yet."
    renderCard={(followup) => (
      <Stack gap={6}>
        <Group gap={6}>
          {labelBadge(followup.status, followupStatusColor(followup.status))}
          {labelBadge(followup.followup_type, "violet")}
          {labelBadge(followup.priority, "orange")}
        </Group>
        <Text size="xs" c="dimmed">
          Due: {followup.due_date ? String(followup.due_date).slice(0, 10) : "—"}
        </Text>
        <Text size="sm" fw={500}>
          {followup.person_name}
          {followup.phone ? ` · ${followup.phone}` : ""}
        </Text>
        {followup.notes ? (
          <Text size="sm" c="dimmed">{followup.notes}</Text>
        ) : null}
      </Stack>
    )}
  />
);

export const LeadEventTimeline = ({ items = [], isLoading }) => {
  if (isLoading) return <Text size="sm" c="dimmed">Loading...</Text>;
  if (!items.length) return <Text size="sm" c="dimmed">No timeline events yet.</Text>;

  return (
    <Timeline active={items.length} bulletSize={12} lineWidth={2}>
      {items.map((event) => (
        <Timeline.Item key={event.id} color={EVENT_COLOR[event.event_type] || "gray"} title={event.title}>
          {event.description ? <Text size="sm">{event.description}</Text> : null}
          {Object.entries(event.metadata?.changes || {}).map(([field, diff]) => (
            <Text key={field} size="xs" tt="capitalize">
              {field.replaceAll("_", " ")}: {metaText(diff?.previous)} → {metaText(diff?.new)}
            </Text>
          ))}
          <Text size="xs" c="dimmed" mt={4}>
            {dayjs(event.created_on).format("DD MMM YYYY, h:mm a")}
            {event.actor?.name ? ` · ${event.actor.name}` : ""}
          </Text>
        </Timeline.Item>
      ))}
    </Timeline>
  );
};
